import React, { useEffect, useState } from "react";
import { useLDClient } from "launchdarkly-react-client-sdk";
import { useSelector } from "react-redux";
import { RootState } from "@reducers/rootReducer";
import { useV2GetAccountQuery, useV2WhoAmIQuery } from "@generated";
import ScopeStackSpinner from "@components/ScopeStackSpinner/ScopeStackSpinner";

// `uuid` is serialized by V2::MeResource and V2::AccountResource but missing
// from their swagger schemas, so it isn't on the generated types.
type WithUuid = { uuid?: string };

// `accountSlug` is still accepted from callers, but v2 resolves the account
// from the bearer token. `{}` matches the other /v2/account callers so they
// share one cache entry.
const LaunchDarklyProvider = ({
  children,
}: {
  children: React.ReactNode;
  accountSlug?: string;
}) => {
  const ldClient = useLDClient();
  const userSignedIn = useSelector((state: RootState) => state.auth.authorized);
  const { data: meData } = useV2WhoAmIQuery();
  const { data: accountData } = useV2GetAccountQuery({});
  const currentUser = meData?.data;
  const account = accountData?.data;
  const [identified, setIdentified] = useState(false);

  useEffect(() => {
    if (userSignedIn && currentUser && account && ldClient) {
      const context = {
        user: {
          key: (currentUser.attributes as WithUuid).uuid,
          email: currentUser.attributes.email,
          name: currentUser.attributes.name,
        },
        organization: {
          key: (account.attributes as WithUuid | undefined)?.uuid,
          name: account.attributes?.name,
        },
        kind: "multi",
      };

      // Fallback: if identify hangs (e.g. dev/dummyclient), unblock after 3s
      const timeout = setTimeout(() => setIdentified(true), 3000);
      ldClient
        .identify(context)
        .then(() => setIdentified(true))
        .catch(() => setIdentified(true))
        .finally(() => clearTimeout(timeout));
    }
  }, [ldClient, currentUser, account, userSignedIn]);

  if (!identified) return <ScopeStackSpinner />;

  return <>{children}</>;
};

export default LaunchDarklyProvider;
